import { v4 as uuidv4 } from 'uuid';
import { API_BASE_URL } from '../config/apiBaseUrl';
import { logging } from './logging';

type AnalyticsConfig = {
  endpoint: string;
  enabled: boolean;
  appVersion?: string;
  appName?: string;
};

type AnalyticsIdentity = {
  userId: string | null;
  anonymousId: string | null;
};

type TrackPayload = {
  eventName: string;
  properties?: Record<string, unknown>;
  context?: Record<string, unknown>;
  timestamp: string;
  userId?: string | null;
  anonymousId?: string | null;
  sessionId?: string;
  appVersion?: string;
  appName?: string;
  screen?: string;
};

const RUNTIME_MODE = import.meta.env.MODE ?? 'development';
const IS_TEST_MODE = RUNTIME_MODE === 'test';
const DEFAULT_ENDPOINT = import.meta.env.VITE_ANALYTICS_URL || `${API_BASE_URL}/analytics/events`;
const DEFAULT_ENABLED = !IS_TEST_MODE && import.meta.env.VITE_ANALYTICS_ENABLED !== 'false';
const MAX_BATCH_SIZE = 5;
const MAX_BATCH_AGE_MS = 5_000;

let config: AnalyticsConfig = {
  endpoint: DEFAULT_ENDPOINT,
  enabled: DEFAULT_ENABLED,
};

let identity: AnalyticsIdentity = {
  userId: null,
  anonymousId: null,
};

let queue: TrackPayload[] = [];
let pendingKeys = new Set<string>();
let flushTimer: number | null = null;

const getOS = () => {
  if (typeof window === 'undefined') return undefined;
  const userAgent = window.navigator.userAgent;
  let os = 'Unknown';
  let version = '';

  if (userAgent.indexOf('Win') !== -1) {
    os = 'Windows';
    if (userAgent.indexOf('Windows NT 10.0') !== -1) version = '10/11';
    else if (userAgent.indexOf('Windows NT 6.3') !== -1) version = '8.1';
    else if (userAgent.indexOf('Windows NT 6.2') !== -1) version = '8';
    else if (userAgent.indexOf('Windows NT 6.1') !== -1) version = '7';
  } else if (userAgent.indexOf('Android') !== -1) {
    os = 'Android';
    version = userAgent.split('Android ')[1]?.split(';')[0] || '';
  } else if (userAgent.indexOf('Mac') !== -1) {
    os = userAgent.indexOf('iPhone') !== -1 || userAgent.indexOf('iPad') !== -1 ? 'iOS' : 'MacOS';
    version = userAgent.split('OS ')[1]?.split(' ')[0]?.replace(/_/g, '.') || '';
  } else if (userAgent.indexOf('Linux') !== -1) {
    os = 'Linux';
  } else if (userAgent.indexOf('X11') !== -1) {
    os = 'UNIX';
  }

  return version ? `${os} ${version}` : os;
};

const getBrowser = () => {
  if (typeof window === 'undefined') return undefined;
  const userAgent = window.navigator.userAgent;
  let browser = 'Unknown';
  let version = '';

  if (userAgent.indexOf('Firefox') !== -1) {
    browser = 'Firefox';
    version = userAgent.split('Firefox/')[1] || '';
  } else if (userAgent.indexOf('SamsungBrowser') !== -1) {
    browser = 'Samsung Browser';
    version = userAgent.split('SamsungBrowser/')[1] || '';
  } else if (userAgent.indexOf('Opera') !== -1 || userAgent.indexOf('OPR') !== -1) {
    browser = 'Opera';
    version = userAgent.split('OPR/')[1] || userAgent.split('Opera/')[1] || '';
  } else if (userAgent.indexOf('Trident') !== -1) {
    browser = 'Internet Explorer';
    version = userAgent.split('rv:')[1] || '';
  } else if (userAgent.indexOf('Edge') !== -1) {
    browser = 'Edge';
    version = userAgent.split('Edge/')[1] || '';
  } else if (userAgent.indexOf('Chrome') !== -1) {
    browser = 'Chrome';
    version = userAgent.split('Chrome/')[1]?.split(' ')[0] || '';
  } else if (userAgent.indexOf('Safari') !== -1) {
    browser = 'Safari';
    version = userAgent.split('Version/')[1]?.split(' ')[0] || '';
  }

  return version ? `${browser} ${version}` : browser;
};

const getSessionId = () => {
  if (typeof window === 'undefined') return undefined;
  const key = 'analytics.sessionId';
  const existing = window.sessionStorage.getItem(key);
  if (existing) return existing;
  const next = uuidv4();
  window.sessionStorage.setItem(key, next);
  return next;
};

const sendPayloads = (payloads: TrackPayload[]) => {
  if (!config.enabled || typeof window === 'undefined') return;
  if (!payloads.length) return;
  const body = JSON.stringify({ events: payloads });
  if (navigator.sendBeacon) {
    const blob = new Blob([body], { type: 'application/json' });
    navigator.sendBeacon(config.endpoint, blob);
    return;
  }

  void fetch(config.endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
    credentials: 'include',
  }).catch(error => {
    if (!IS_TEST_MODE) {
      logging.warn('[analytics] Failed to send events', { error });
    }
  });
};

const flushQueue = () => {
  if (!queue.length) return;
  const payloads = queue;
  queue = [];
  pendingKeys.clear();
  if (flushTimer) {
    window.clearTimeout(flushTimer);
    flushTimer = null;
  }
  sendPayloads(payloads);
};

const scheduleFlush = () => {
  if (flushTimer || typeof window === 'undefined') return;
  flushTimer = window.setTimeout(() => {
    flushQueue();
  }, MAX_BATCH_AGE_MS);
};

const getDedupeKey = (payload: TrackPayload) => {
  const props = payload.properties ? JSON.stringify(payload.properties) : '';
  const context = payload.context ? JSON.stringify(payload.context) : '';
  return `${payload.eventName}|${payload.screen ?? ''}|${props}|${context}`;
};

const buildPayload = (
  eventName: string,
  properties?: Record<string, unknown>,
  context?: Record<string, unknown>
): TrackPayload => {
  const screen = typeof window !== 'undefined' ? window.location.pathname : undefined;
  return {
    eventName,
    properties,
    context,
    timestamp: new Date().toISOString(),
    userId: identity.userId,
    anonymousId: identity.anonymousId,
    sessionId: getSessionId(),
    appVersion: config.appVersion,
    appName: config.appName,
    screen,
  };
};

export const analytics = {
  init: (overrides: Partial<AnalyticsConfig>) => {
    config = { ...config, ...overrides };
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', flushQueue);
      window.addEventListener('pagehide', flushQueue);
    }
  },
  setIdentity: (next: AnalyticsIdentity) => {
    identity = { ...identity, ...next };
  },
  track: (eventName: string, properties?: Record<string, unknown>, context?: Record<string, unknown>) => {
    if (!config.enabled) return;
    const payload = buildPayload(eventName, properties, context);
    const key = getDedupeKey(payload);
    if (pendingKeys.has(key)) return;
    pendingKeys.add(key);
    queue.push(payload);
    if (queue.length >= MAX_BATCH_SIZE) {
      flushQueue();
      return;
    }
    scheduleFlush();
  },
  page: (screen: string, properties?: Record<string, unknown>) => {
    const userDetails = {
      os: getOS(),
      browser: getBrowser(),
    };
    analytics.track('navigation: route_change', { screen, ...userDetails, ...properties });
  },
};
